<template>
  <div class="message-input" :class="inputClasses">
    <!-- File Attachments Preview -->
    <div v-if="attachments.length > 0" class="attachments-preview">
      <div
        v-for="(attachment, index) in attachments"
        :key="index"
        class="attachment-preview"
      >
        <div class="attachment-info">
          <span class="attachment-icon">📎</span>
          <span class="attachment-name">{{ attachment.name }}</span>
          <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
        </div>
        <button
          @click="removeAttachment(index)"
          class="remove-attachment"
          title="Remove attachment"
        >
          ✕
        </button>
      </div>
    </div>

    <!-- Input Area -->
    <div class="input-area">
      <!-- Attachment Button -->
      <button
        v-if="showAttachmentButton"
        @click="triggerFileInput"
        class="input-button attachment-button"
        title="Attach file"
        :disabled="disabled"
      >
        📎
      </button>

      <!-- Text Input -->
      <div class="input-wrapper">
        <textarea
          ref="textInput"
          v-model="inputValue"
          :placeholder="placeholder"
          :disabled="disabled"
          :maxlength="maxLength"
          class="text-input"
          rows="1"
          @input="handleInput"
          @keydown="handleKeyDown"
          @focus="handleFocus"
          @blur="handleBlur"
          @paste="handlePaste"
        />
        <div v-if="maxLength" class="character-count">
          {{ inputValue.length }}/{{ maxLength }}
        </div>
      </div>

      <!-- Emoji Button -->
      <button
        v-if="showEmojiButton"
        @click="toggleEmojiPicker"
        class="input-button emoji-button"
        title="Add emoji"
        :disabled="disabled"
      >
        😊
      </button>

      <!-- Send Button -->
      <button
        v-if="showSendButton"
        @click="sendMessage"
        class="send-button"
        :class="{ 'send-button--active': canSend }"
        :disabled="disabled || !canSend"
        title="Send message (Enter)"
      >
        <span v-if="isSending" class="sending-icon">⏳</span>
        <span v-else>➤</span>
      </button>
    </div>

    <!-- Emoji Picker -->
    <div v-if="showEmojiPicker" class="emoji-picker" ref="emojiPicker">
      <div class="emoji-grid">
        <button
          v-for="emoji in availableEmojis"
          :key="emoji"
          @click="insertEmoji(emoji)"
          class="emoji-button-grid"
          :title="emoji"
        >
          {{ emoji }}
        </button>
      </div>
    </div>

    <!-- File Input (Hidden) -->
    <input
      ref="fileInput"
      type="file"
      multiple
      accept="image/*,.pdf,.doc,.docx,.txt"
      class="file-input"
      @change="handleFileSelect"
    />

    <!-- Drag & Drop Overlay -->
    <div
      v-if="isDragOver"
      class="drag-overlay"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @dragleave="isDragOver = false"
    >
      <div class="drag-content">
        <div class="drag-icon">📁</div>
        <p>Drop files here to attach</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'

export default {
  name: 'MessageInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Type a message...'
    },
    maxLength: {
      type: Number,
      default: null
    },
    showSendButton: {
      type: Boolean,
      default: true
    },
    showAttachmentButton: {
      type: Boolean,
      default: true
    },
    showEmojiButton: {
      type: Boolean,
      default: true
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024 // 10MB
    },
    maxAttachments: {
      type: Number,
      default: 5
    }
  },
  emits: [
    'update:modelValue',
    'send',
    'attachment',
    'typing-start',
    'typing-stop'
  ],
  setup(props, { emit }) {
    // Références
    const textInput = ref(null)
    const fileInput = ref(null)
    const emojiPicker = ref(null)

    // État local
    const inputValue = ref(props.modelValue)
    const attachments = ref([])
    const isSending = ref(false)
    const showEmojiPicker = ref(false)
    const isDragOver = ref(false)
    const typingTimeout = ref(null)

    // Émojis disponibles
    const availableEmojis = [
      '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰',
      '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨',
      '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒',
      '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕',
      '😟', '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭',
      '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀',
      '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖', '❤️', '🧡', '💛', '💚', '💙', '💜',
      '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '👍', '👎', '👌',
      '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋',
      '🤏', '✍️', '👏', '🙌', '👐', '🤲', '🙏', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃',
      '🧠', '🦷', '🦴', '👀', '👁️', '👅', '👄', '💋', '🩸'
    ]

    // Computed
    const inputClasses = computed(() => ({
      'message-input--disabled': props.disabled,
      'message-input--has-attachments': attachments.value.length > 0,
      'message-input--focused': textInput.value === document.activeElement
    }))

    const canSend = computed(() => {
      return (
        inputValue.value.trim().length > 0 ||
        attachments.value.length > 0
      ) && !props.disabled && !isSending.value
    })

    // Méthodes
    const adjustTextareaHeight = () => {
      if (!textInput.value) return

      textInput.value.style.height = 'auto'
      const maxHeight = 120 // 5 lignes environ
      const newHeight = Math.min(textInput.value.scrollHeight, maxHeight)
      textInput.value.style.height = `${newHeight}px`
    }

    const handleInput = (event) => {
      inputValue.value = event.target.value
      emit('update:modelValue', inputValue.value)

      // Ajuster la hauteur du textarea
      nextTick(() => {
        adjustTextareaHeight()
      })

      // Gérer l'indicateur de typing
      handleTypingStart()

      // Nettoyer le timeout précédent
      if (typingTimeout.value) {
        clearTimeout(typingTimeout.value)
      }

      // Arrêter l'indicateur après 1 seconde d'inactivité
      typingTimeout.value = setTimeout(() => {
        handleTypingStop()
      }, 1000)
    }

    const handleKeyDown = (event) => {
      if (props.disabled) return

      switch (event.key) {
        case 'Enter':
          if (!event.shiftKey) {
            event.preventDefault()
            sendMessage()
          }
          break
        case 'Escape':
          if (showEmojiPicker.value) {
            toggleEmojiPicker()
          }
          break
      }
    }

    const handleFocus = () => {
      // Focus handling
    }

    const handleBlur = () => {
      handleTypingStop()
    }

    const handleTypingStart = () => {
      if (inputValue.value.trim().length > 0) {
        emit('typing-start')
      }
    }

    const handleTypingStop = () => {
      emit('typing-stop')
    }

    const sendMessage = async () => {
      if (!canSend.value) return

      isSending.value = true

      try {
        const messageText = inputValue.value.trim()
        const attachmentData = attachments.value.length > 0 ? [...attachments.value] : []

        emit('send', messageText, attachmentData)

        // Réinitialiser l'input
        inputValue.value = ''
        attachments.value = []
        emit('update:modelValue', '')

        // Réinitialiser la hauteur du textarea
        nextTick(() => {
          if (textInput.value) {
            textInput.value.style.height = 'auto'
          }
        })

        // Remettre le focus
        nextTick(() => {
          if (textInput.value) {
            textInput.value.focus()
          }
        })

      } finally {
        isSending.value = false
      }
    }

    const triggerFileInput = () => {
      if (fileInput.value) {
        fileInput.value.click()
      }
    }

    const handleFileSelect = (event) => {
      const files = Array.from(event.target.files)
      addAttachments(files)

      // Réinitialiser l'input pour permettre de sélectionner les mêmes fichiers
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const handlePaste = (event) => {
      const items = Array.from(event.clipboardData.items)
      const files = items
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
        .filter(Boolean)

      if (files.length > 0) {
        event.preventDefault()
        addAttachments(files)
      }
    }

    const addAttachments = (files) => {
      const validFiles = files.filter(file => {
        if (file.size > props.maxFileSize) {
          console.warn(`File ${file.name} is too large`)
          return false
        }
        return true
      })

      // Limiter le nombre de pièces jointes
      const remainingSlots = props.maxAttachments - attachments.value.length
      const filesToAdd = validFiles.slice(0, remainingSlots)

      filesToAdd.forEach(file => {
        const attachment = {
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
          url: URL.createObjectURL(file)
        }
        attachments.value.push(attachment)
      })

      if (filesToAdd.length > 0) {
        emit('attachment', filesToAdd)
      }
    }

    const removeAttachment = (index) => {
      const attachment = attachments.value[index]

      // Libérer l'URL blob si elle existe
      if (attachment.url && attachment.url.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.url)
      }

      attachments.value.splice(index, 1)
    }

    const toggleEmojiPicker = () => {
      showEmojiPicker.value = !showEmojiPicker.value

      if (showEmojiPicker.value) {
        nextTick(() => {
          // Positionner le picker
          if (emojiPicker.value && textInput.value) {
            const rect = textInput.value.getBoundingClientRect()
            emojiPicker.value.style.bottom = `${rect.height + 8}px`
            emojiPicker.value.style.right = '0'
          }
        })
      }
    }

    const insertEmoji = (emoji) => {
      const textarea = textInput.value
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = inputValue.value

      const newText = text.substring(0, start) + emoji + text.substring(end)
      inputValue.value = newText
      emit('update:modelValue', newText)

      // Positionner le curseur après l'emoji
      nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length
        textarea.focus()
      })

      showEmojiPicker.value = false
    }

    const handleDrop = (event) => {
      isDragOver.value = false

      const files = Array.from(event.dataTransfer.files)
      addAttachments(files)
    }

    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B'

      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }

      return `${size.toFixed(1)} ${units[unitIndex]}`
    }

    // Clic en dehors du emoji picker
    const handleClickOutside = (event) => {
      if (
        showEmojiPicker.value &&
        emojiPicker.value &&
        !emojiPicker.value.contains(event.target) &&
        !event.target.closest('.emoji-button')
      ) {
        showEmojiPicker.value = false
      }
    }

    // Watchers
    watch(() => props.modelValue, (newValue) => {
      if (newValue !== inputValue.value) {
        inputValue.value = newValue
        nextTick(() => {
          adjustTextareaHeight()
        })
      }
    })

    // Cycle de vie
    onMounted(() => {
      document.addEventListener('click', handleClickOutside)

      // Drag & drop sur toute la fenêtre
      document.addEventListener('dragover', (e) => {
        e.preventDefault()
        isDragOver.value = true
      })

      document.addEventListener('dragleave', (e) => {
        if (e.clientX === 0 && e.clientY === 0) {
          isDragOver.value = false
        }
      })

      document.addEventListener('drop', (e) => {
        if (isDragOver.value) {
          e.preventDefault()
          isDragOver.value = false
        }
      })
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)

      // Nettoyer les URLs blob
      attachments.value.forEach(attachment => {
        if (attachment.url && attachment.url.startsWith('blob:')) {
          URL.revokeObjectURL(attachment.url)
        }
      })

      if (typingTimeout.value) {
        clearTimeout(typingTimeout.value)
      }
    })

    return {
      // Références
      textInput,
      fileInput,
      emojiPicker,

      // État
      inputValue,
      attachments,
      isSending,
      showEmojiPicker,
      isDragOver,
      availableEmojis,

      // Computed
      inputClasses,
      canSend,

      // Méthodes
      handleInput,
      handleKeyDown,
      handleFocus,
      handleBlur,
      sendMessage,
      triggerFileInput,
      handleFileSelect,
      handlePaste,
      removeAttachment,
      toggleEmojiPicker,
      insertEmoji,
      handleDrop,
      formatFileSize
    }
  }
}
</script>

<style scoped>
.message-input {
  position: relative;
  background: white;
  border-radius: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.message-input--focused {
  box-shadow: 0 2px 12px rgba(59, 130, 246, 0.2);
}

.message-input--disabled {
  opacity: 0.6;
  pointer-events: none;
}

.attachments-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem 1rem 0.5rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.attachment-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.attachment-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.attachment-icon {
  flex-shrink: 0;
  font-size: 1rem;
}

.attachment-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.attachment-size {
  font-size: 0.75rem;
  color: #6b7280;
  flex-shrink: 0;
}

.remove-attachment {
  width: 20px;
  height: 20px;
  border: none;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background 0.2s;
  flex-shrink: 0;
}

.remove-attachment:hover {
  background: #dc2626;
}

.input-area {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
}

.input-button {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.input-button:hover:not(:disabled) {
  background: #f3f4f6;
}

.input-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.input-wrapper {
  flex: 1;
  position: relative;
  min-width: 0;
}

.text-input {
  width: 100%;
  min-height: 36px;
  max-height: 120px;
  border: none;
  outline: none;
  resize: none;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #1f2937;
  background: transparent;
  font-family: inherit;
}

.text-input::placeholder {
  color: #9ca3af;
}

.text-input:disabled {
  opacity: 0.6;
}

.character-count {
  position: absolute;
  bottom: -20px;
  right: 0;
  font-size: 0.75rem;
  color: #9ca3af;
}

.send-button {
  width: 36px;
  height: 36px;
  border: none;
  background: #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
  flex-shrink: 0;
}

.send-button--active {
  background: hsl(var(--primary));
  color: white;
}

.send-button--active:hover {
  background: hsl(var(--primary) / 0.9);
  transform: scale(1.05);
}

.sending-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.emoji-picker {
  position: absolute;
  bottom: 100%;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 0.75rem;
  z-index: 1000;
  margin-bottom: 0.5rem;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 0.25rem;
  max-width: 320px;
  max-height: 200px;
  overflow-y: auto;
}

.emoji-button-grid {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.25rem;
  transition: background 0.2s;
}

.emoji-button-grid:hover {
  background: #f3f4f6;
}

.file-input {
  display: none;
}

.drag-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.drag-content {
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  border: 2px dashed hsl(var(--primary));
}

.drag-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.drag-content p {
  margin: 0;
  color: #6b7280;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 640px) {
  .input-area {
    padding: 0.625rem 0.75rem;
    gap: 0.375rem;
  }

  .input-button {
    width: 32px;
    height: 32px;
    font-size: 1.125rem;
  }

  .send-button {
    width: 32px;
    height: 32px;
  }

  .text-input {
    font-size: 0.875rem;
    min-height: 32px;
  }

  .emoji-picker {
    right: -1rem;
    bottom: 100%;
  }

  .emoji-grid {
    grid-template-columns: repeat(6, 1fr);
    max-width: 240px;
  }
}
</style>